// Units + Residents steps of the onboarding wizard (steps 04 and 05).
// Markup is class-based; all visual rules live in admin.css.

import { useState } from "react";
import type { Bathrooms, Bedrooms, BoardSeat, GarageSize, Relationship, Resident, Unit } from "./types";
import type { DirectoryApi } from "./useDirectory";

const BEDROOMS: Bedrooms[] = ["1", "2", "3", "4", "5"];
const BATHROOMS: Bathrooms[] = ["1", "1.5", "2", "2.5", "3"];
const GARAGES: GarageSize[] = ["None", "1", "2", "3", "4", "5"];
const SEATS: BoardSeat[] = ["President", "Treasurer", "Secretary", "Member at large"];

function Segmented<T extends string>({ label, options, value, onChange }: {
  label: string; options: readonly T[]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="bq-field">
      <span className="bq-label">{label}</span>
      <div className="bq-seg" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={o === value}
            className={"bq-seg__opt" + (o === value ? " is-on" : "")}
            onClick={() => onChange(o)}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function Check({ on, onToggle, children }: { on: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={"bq-check" + (on ? " is-on" : "")} aria-pressed={on} onClick={onToggle}>
      <span className="bq-check__box" aria-hidden="true" />
      {children}
    </button>
  );
}

export function CsvBar({ columns, onDownload, onUpload }: {
  columns: string; onDownload: () => void; onUpload: (file: File) => void;
}) {
  return (
    <div className="bq-csv">
      <span className="bq-csv__cols">CSV · {columns}</span>
      <span className="bq-csv__actions">
        <button type="button" className="bq-btn bq-btn--ghost" onClick={onDownload}>Download</button>
        <label className="bq-btn bq-btn--ink">
          Upload CSV
          <input
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.currentTarget.value = "";
            }}
          />
        </label>
      </span>
    </div>
  );
}

const EMPTY_UNIT = { addr: "", beds: "3" as Bedrooms, baths: "2" as Bathrooms, patio: false, garage: "2" as GarageSize, plates: "" };

export function UnitsStep({ dir }: { dir: DirectoryApi }) {
  const [d, setD] = useState(EMPTY_UNIT);
  const canAdd = d.addr.trim().length > 2;

  const submit = () => {
    if (!canAdd) return;
    dir.addUnit({
      addr: d.addr.trim(), beds: d.beds, baths: d.baths, patio: d.patio, garage: d.garage,
      plates: d.plates.split(/[|,]/).map((p) => p.trim().toUpperCase()).filter(Boolean),
    });
    setD({ ...d, addr: "", plates: "" });
  };

  return (
    <section className="bq-step">
      <header className="bq-step__head">
        <h2 className="bq-step__title">Unit directory</h2>
        <p className="bq-step__note">
          One record per door. Residents are linked to these units on the next step, and every request a
          resident files is filed against the unit.
        </p>
      </header>

      <CsvBar
        columns="address, bedrooms, bathrooms, patio, garage, license_plates"
        onDownload={dir.exportUnits}
        onUpload={(f) => { void dir.importUnits(f); }}
      />

      <form className="bq-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="bq-field">
          <span className="bq-label">Street address</span>
          <input className="bq-input" value={d.addr} onChange={(e) => setD({ ...d, addr: e.target.value })} placeholder="1424 Oakridge Way" />
        </label>

        <div className="bq-row">
          <Segmented label="Bedrooms" options={BEDROOMS} value={d.beds} onChange={(v) => setD({ ...d, beds: v })} />
          <Segmented label="Bathrooms" options={BATHROOMS} value={d.baths} onChange={(v) => setD({ ...d, baths: v })} />
        </div>

        <div className="bq-row bq-row--end">
          <Segmented label="Garage" options={GARAGES} value={d.garage} onChange={(v) => setD({ ...d, garage: v })} />
          <Check on={d.patio} onToggle={() => setD({ ...d, patio: !d.patio })}>Patio</Check>
        </div>

        <label className="bq-field">
          <span className="bq-label">License plates</span>
          <input className="bq-input bq-input--mono" value={d.plates} onChange={(e) => setD({ ...d, plates: e.target.value })} placeholder="8XKF412, 7BQL093 — comma separated" />
        </label>

        <button type="submit" className="bq-btn bq-btn--accent" disabled={!canAdd}>Add unit</button>
      </form>

      <div className="bq-listhead">
        <span className="bq-label">Units on file</span>
        <span className="bq-count">{dir.units.length} {dir.units.length === 1 ? "unit" : "units"}</span>
      </div>

      {dir.units.length === 0 && <p className="bq-empty">No units added yet. Add them one at a time, or upload a CSV.</p>}

      <ul className="bq-list">
        {dir.units.map((u: Unit, i: number) => {
          const held = dir.residentsOf(u.id);
          return (
            <li className="bq-list__row" key={u.id}>
              <span className="bq-list__n">{String(i + 1).padStart(2, "0")}</span>
              <span className="bq-list__body">
                <span className="bq-list__title">{u.addr}</span>
                <span className="bq-list__meta">
                  {u.beds} bd · {u.baths} ba{u.patio ? " · patio" : ""} · {u.garage === "None" ? "no garage" : u.garage + "-car garage"}
                </span>
                <span className="bq-list__plates">{u.plates.length ? u.plates.join("  ·  ") : "No plates on file"}</span>
                <span className="bq-list__meta">
                  {held.length ? held.map((r) => r.name + (r.board ? " (board)" : "")).join(", ") : "No resident linked yet"}
                </span>
              </span>
              <button type="button" className="bq-x" aria-label={"Remove " + u.addr} onClick={() => dir.removeUnit(u.id)}>×</button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const EMPTY_RESIDENT = {
  name: "", email: "", phone: "",
  rel: "Owner" as Relationship, board: false, role: "Member at large" as BoardSeat, units: [] as string[],
};

export function ResidentsStep({ dir }: { dir: DirectoryApi }) {
  const [d, setD] = useState(EMPTY_RESIDENT);
  const canAdd = d.name.trim().length > 1 && d.units.length > 0;

  const submit = () => {
    if (!canAdd) return;
    dir.addResident({
      name: d.name.trim(), email: d.email.trim(), phone: d.phone.trim(),
      rel: d.rel, board: d.board, role: d.board ? d.role : "", units: [...d.units],
    });
    setD(EMPTY_RESIDENT);
  };

  const toggleUnit = (id: string) =>
    setD((p) => ({ ...p, units: p.units.includes(id) ? p.units.filter((x) => x !== id) : [...p.units, id] }));

  return (
    <section className="bq-step">
      <header className="bq-step__head">
        <h2 className="bq-step__title">Resident directory</h2>
        <p className="bq-step__note">
          Each resident is linked to one or more units. Mark a resident as a board member and their sign-in
          opens the board view; everyone else lands in the resident view.
        </p>
      </header>

      <CsvBar
        columns="name, email, phone, relationship, board_member, board_seat, units"
        onDownload={dir.exportResidents}
        onUpload={(f) => { void dir.importResidents(f); }}
      />

      <form className="bq-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="bq-field">
          <span className="bq-label">Full name</span>
          <input className="bq-input" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Marta Fiore" />
        </label>

        <div className="bq-row">
          <label className="bq-field bq-field--grow">
            <span className="bq-label">Email</span>
            <input className="bq-input" type="email" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} placeholder="m.fiore@example.com" />
          </label>
          <label className="bq-field bq-field--grow">
            <span className="bq-label">Phone</span>
            <input className="bq-input" type="tel" value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} placeholder="(408) 555-0142" />
          </label>
        </div>

        <div className="bq-row bq-row--end">
          <Segmented label="Relationship" options={["Owner", "Tenant"] as const} value={d.rel} onChange={(v) => setD({ ...d, rel: v })} />
          <Check on={d.board} onToggle={() => setD({ ...d, board: !d.board })}>Board member</Check>
        </div>

        {d.board && <Segmented label="Board seat" options={SEATS} value={d.role} onChange={(v) => setD({ ...d, role: v })} />}

        <div className="bq-field">
          <span className="bq-label">Units</span>
          {dir.units.length === 0
            ? <p className="bq-empty">Add at least one unit on step 04 before linking residents.</p>
            : (
              <div className="bq-chips">
                {dir.units.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    aria-pressed={d.units.includes(u.id)}
                    className={"bq-chip" + (d.units.includes(u.id) ? " is-on" : "")}
                    onClick={() => toggleUnit(u.id)}
                  >
                    {u.addr}
                  </button>
                ))}
              </div>
            )}
        </div>

        <p className="bq-callout">
          <span>On sign-in this person opens</span>
          <strong>{d.board ? "Board view" : "Resident view"}</strong>
        </p>

        <button type="submit" className="bq-btn bq-btn--accent" disabled={!canAdd}>Add resident</button>
      </form>

      <div className="bq-listhead">
        <span className="bq-label">Residents on file</span>
        <span className="bq-count">{dir.residents.length} · {dir.boardCount} with board access</span>
      </div>

      {dir.residents.length === 0 && <p className="bq-empty">No residents added yet.</p>}

      <ul className="bq-list">
        {dir.residents.map((r: Resident) => (
          <li className="bq-list__row" key={r.id}>
            <span className="bq-list__body">
              <span className="bq-list__titlerow">
                <span className="bq-list__title">{r.name}</span>
                {r.board && <span className="bq-tag bq-tag--accent">{r.role}</span>}
                <span className="bq-tag">{r.rel}</span>
              </span>
              <span className="bq-list__meta">{[r.email, r.phone].filter(Boolean).join(" · ") || "No contact on file"}</span>
              <span className="bq-list__units">
                {r.units.map((id) => dir.units.find((u) => u.id === id)?.addr).filter(Boolean).join(", ") || "—"}
              </span>
              <span className="bq-list__view">Signs in to {r.board ? "Board view" : "Resident view"}</span>
            </span>
            <button type="button" className="bq-x" aria-label={"Remove " + r.name} onClick={() => dir.removeResident(r.id)}>×</button>
          </li>
        ))}
      </ul>
    </section>
  );
}
